import { useQuery, useQueryClient } from "@tanstack/react-query";
import { pick } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import Collapsible from "react-collapsible";
import useScrollToSummary from "../../../../../hooks/useScrollToSummary";
import useIntegrationConnectionStatus from "../../../../integrations/hooks/useIntegrationConnectionStatus";
import { route } from "../../../../../shim/jsbridge";
import {
  errorCallback,
  reanalyze,
  reanalyzeStatus
} from "../analysistable.service";
import CapeTableRow from "./cape/CapeTableRow";
import CiscoSecureMalwareAnalyticsTableRow from "./cisco_secure_malware_analytics/CiscoSecureMalwareAnalyticsTableRow";
import CuckooTableRow from "./cuckoo/CuckooTableRow";
import FireEyeTableRow from "./fireeye/FireEyeTableRow";
import JoeTableRow from "./joe/JoeTableRow";
import VmRayTableRow from "./vmray/VmRayTableRow";

import { ANALYSIS_STATUS_QUERY_KEY } from "../../../hooks/useGetSampleAnalysisStatus";
import { INITIAL_QUERY_DATA } from "../consts";

const INTEGRATIONS_ACCORDION_CONTAINER_ID = "integrations-analysis-accordion";

const DEFAULT_REFRESH_RATE = 10 * 1000;

export const GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY =
  "getSampleDetailsAnalysisStatus";

const IntegrationsAnalysisTable = ({
  sha1,
  sampleSourceID,
  resultCape,
  resultCiscoSecureMalwareAnalytics,
  resultCuckoo,
  resultJoe,
  resultFireeye,
  resultVmRay,
  reanalyzePopupWindowSubmitData,
  scrollStateTarget,
  runSampleSummaryDataApi,
  relationshipID
}) => {
  const queryClient = useQueryClient();
  const [isAccordion, setIsAccordion] = useState(false);
  const abortControllers = useRef([]);

  // TODO Refactor this so that IntegrationsAnalysisTable uses same analysis status API as ReportSummarySubheader
  const getAnalysisStatus = hash => {
    const abortController = new AbortController();
    abortControllers.current.push(abortController);
    return reanalyzeStatus(hash, abortController.signal)
      .then(data => {
        const previousStatus = queryClient.getQueryData([
          GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY,
          sha1
        ]);

        const integrations = pick(data, [
          "cape",
          "cuckoo",
          "fireeye",
          "joe",
          "cisco_secure_malware_analytics",
          "vmray_tcbase"
        ]);
        const shouldRefetchSummary = Object.keys(integrations).reduce(
          (acc, key) =>
            acc ||
            (previousStatus[key]?.isProcessing !== null &&
              data[key]?.isProcessing !== previousStatus[key]?.isProcessing),
          false
        );

        if (shouldRefetchSummary) {
          runSampleSummaryDataApi();
        }
        return data;
      })
      .catch(err => {
        errorCallback(err, null);
      });
  };

  const { refetch: refetchAnalysisStatus, data: analysisState } = useQuery({
    queryKey: [GET_SAMPLE_ANALYSIS_STATUS_QUERY_KEY, sha1],
    queryFn: () => getAnalysisStatus(sha1),
    initialData: INITIAL_QUERY_DATA,
    refetchInterval: query => {
      if (!query.state.data) return DEFAULT_REFRESH_RATE;
      const isProcessing = Object.values(query.state.data).some(
        item => item.isProcessing
      );
      if (isProcessing) return DEFAULT_REFRESH_RATE;
      return false;
    }
  });

  useEffect(() => {
    const { current } = abortControllers || [];
    refetchAnalysisStatus();
    return () => current.forEach(controller => controller.abort());
  }, [reanalyzePopupWindowSubmitData]);

  useScrollToSummary({
    scrollStateTarget,
    containerId: INTEGRATIONS_ACCORDION_CONTAINER_ID,
    cb: useCallback(() => setIsAccordion(true), [])
  });

  const isAvailable = useRef(true);
  const { checkIntegrationConnection } =
    useIntegrationConnectionStatus(isAvailable);

  const runIntegrationsReanalyze = (toolShortName, toolTitle) => {
    reanalyze(
      toolTitle,
      {
        samples: [
          {
            hash_value: sha1,
            source_id: sampleSourceID,
            relationship_id: relationshipID
          }
        ],
        [toolShortName]: true,
        dynamic_analysis: [toolShortName]
      },
      () => {
        refetchAnalysisStatus();
        queryClient.refetchQueries({ queryKey: [ANALYSIS_STATUS_QUERY_KEY] });
      }
    );
  };

  const loadConfigScreen = () => {
    window.location = route("integrations");
  };

  const handleIntegrationsAction = async (toolShortName, toolTitle) => {
    const { isConfigured, fileTypeSupported } =
      analysisState[toolShortName] || {};

    if (isConfigured) {
      try {
        await checkIntegrationConnection(toolShortName);
      } catch (error) {
        console.error(
          `${toolTitle} connectivity problems detected. Check the integration configuration for more details.`
        );
      }
      if (!isAvailable.current) return;
    }

    if (!isConfigured || !fileTypeSupported) {
      loadConfigScreen();
    } else {
      runIntegrationsReanalyze(toolShortName, toolTitle);
    }
  };

  return (
    <div
      className="sample-summary-accordion"
      id={INTEGRATIONS_ACCORDION_CONTAINER_ID}
    >
      <Collapsible
        trigger={
          <div className="react-collapsible__heading-container">
            <span className="react-collapsible__heading-title">
              Integrations Analysis
            </span>
          </div>
        }
        handleTriggerClick={() => setIsAccordion(prevState => !prevState)}
        open={isAccordion}
        classParentString="react-collapsible"
        transitionTime={200}
        easing="ease-out"
      >
        <table className="analysis-table">
          <colgroup>
            <col width="40px" />
            <col />
            <col width="30%" />
            <col width="25%" />
            <col width="150px" />
          </colgroup>
          <thead>
            <tr>
              <th className="analysis-table__cell-centered" />
              <th>Analysis Method</th>
              <th className="analysis-table__cell-centered">Analysis Result</th>
              <th>Last Analysis Time</th>
              <th className="analysis-table__cell-centered">Action</th>
            </tr>
          </thead>
          <tbody>
            <CapeTableRow
              sha1={sha1}
              sourceID={sampleSourceID}
              capeData={analysisState?.cape}
              resultCape={resultCape}
              handleAction={handleIntegrationsAction}
            />
            <CiscoSecureMalwareAnalyticsTableRow
              ciscoCiscoSecureMalwareAnalyticsData={
                analysisState?.cisco_secure_malware_analytics
              }
              sha1={sha1}
              resultCiscoSecureMalwareAnalytics={
                resultCiscoSecureMalwareAnalytics
              }
              sourceID={sampleSourceID}
              handleAction={handleIntegrationsAction}
            />
            <CuckooTableRow
              sha1={sha1}
              sourceID={sampleSourceID}
              cuckooData={analysisState?.cuckoo}
              resultCuckoo={resultCuckoo}
              handleAction={handleIntegrationsAction}
            />
            <FireEyeTableRow
              sha1={sha1}
              sourceID={sampleSourceID}
              resultFireEye={resultFireeye}
              fireEyeData={analysisState?.fireeye}
              handleAction={handleIntegrationsAction}
            />
            <JoeTableRow
              joeData={analysisState?.joe}
              sha1={sha1}
              resultJoe={resultJoe}
              sourceID={sampleSourceID}
              handleAction={handleIntegrationsAction}
            />
            <VmRayTableRow
              vmrayData={analysisState?.vmray_tcbase}
              sha1={sha1}
              resultVmRay={resultVmRay}
              sourceID={sampleSourceID}
              handleAction={handleIntegrationsAction}
            />
          </tbody>
        </table>
      </Collapsible>
    </div>
  );
};

IntegrationsAnalysisTable.propTypes = {
  sha1: PropTypes.string.isRequired,
  runSampleSummaryDataApi: PropTypes.func.isRequired,
  resultCape: PropTypes.arrayOf(PropTypes.object),
  resultCiscoSecureMalwareAnalytics: PropTypes.arrayOf(PropTypes.object),
  resultCuckoo: PropTypes.arrayOf(PropTypes.object),
  resultFireeye: PropTypes.arrayOf(PropTypes.object),
  resultJoe: PropTypes.arrayOf(PropTypes.object),
  resultVmRay: PropTypes.arrayOf(PropTypes.object),
  reanalyzePopupWindowSubmitData: PropTypes.shape(),
  sampleSourceID: PropTypes.number.isRequired,
  scrollStateTarget: PropTypes.string,
  relationshipID: PropTypes.number
};

export default IntegrationsAnalysisTable;
